<script>
import { cloneVNode, h } from 'vue'
import moment from 'moment'
import ErrorMsg from '@/common/inputs/ErrorMsg.vue'
import { handlerUseStateVmodel } from '@/common/mixins/handlerUseStateVmodel'
import { obterDataComHoraInicial } from '@/utils/formatadoras/formatadoras'
import {
  COMPONENTES_CATALOGO,
  ehComponenteSeletorImaginii,
  valorDoSeletorEstaPreenchido
} from './validacao-seletor'

const SUPORTED_COMPONENTS = [
  'PTextField', 'PTextFieldLegado', 'PontotelInput', 'PSelectLegado',
  'PDatePicker', 'PTimeField', 'InputFile', 'PontotelSeletor',
  ...COMPONENTES_CATALOGO
]
const COMPONENTES_DE_TEXTO = ['PTextField', 'PTextFieldLegado', 'PontotelInput']
const COMPONENTES_COM_ERRO_INTERNO = [
  'PTextField', 'PontotelInput', 'PDatePicker', 'PontotelSeletor',
  ...COMPONENTES_CATALOGO
]
const COMPONENTES_QUE_MARCAM_TOQUE_AO_ATUALIZAR = [
  'PTextField', 'PTextFieldLegado', 'PontotelInput', 'PDatePicker', 'PTimeField', 'InputFile'
]

export default {
  name: 'Validate',
  emits: ['update:modelValue', 'change'],
  inject: {
    validationScope: { default: null }
  },
  props: {
    componentName: { type: String, default: '' },
    required: { type: Boolean, 'default': true },
    min: { type: Number, 'default': null },
    max: { type: Number, 'default': null },
    minLength: { type: Number, 'default': null },
    maxLength: { type: Number, 'default': null },
    minDate: { type: Date, 'default': null },
    maxDate: { type: Date, 'default': null },
    customError: { type: String, 'default': '' },
    onlyNumbers: { type: Boolean, 'default': false },
    // Para o validacaoRegex: precisa usar um objeto com duas chaves (mensagemDeErro e regex)
    validacaoRegex: { type: Object }
  },
  data () {
    return {
      touched: false,
      componentType: null
    }
  },
  computed: {
    valid () {
      return !this.error
    },
    error () {
      return this.requiredError || this.minMaxError || this.lengthError || this.minDateMaxDateError || this.customError || this.onlyNumbersError || this.erroValidacaoRegex
    },
    requiredError () {
      if (!this.required) return ''

      if (ehComponenteSeletorImaginii(this.componentType)) {
        return valorDoSeletorEstaPreenchido(this.state, this.componentType)
          ? ''
          : this.$t('compartilhados.validations.Validate.campoObrigatorio')
      }
      if (this.componentType === 'PSelectLegado') {
        return this.state?.selected ? '' : this.$t('compartilhados.validations.Validate.campoObrigatorio')
      }
      if (this.componentType === 'InputFile') {
        return this.state ? '' : this.$t('compartilhados.validations.Validate.campoObrigatorio')
      }
      return !this.state ? this.$t('compartilhados.validations.Validate.campoObrigatorio') : ''
    },
    minMaxError () {
      if (!COMPONENTES_DE_TEXTO.includes(this.componentType)) return ''

      const number = parseFloat(this.state)
      if ((this.min !== null || this.max !== null) && !isNaN(number)) {
        const validMin = this.min !== null ? number >= this.min : true
        const validMax = this.max !== null ? number <= this.max : true

        if (this.min !== null && this.max !== null) {
          return validMin && validMax ? '' : this.$t('compartilhados.validations.Validate.entre', { min: this.min, max: this.max })
        } else if (this.max !== null) {
          return validMax ? '' : this.$t('compartilhados.validations.Validate.menorQue', { max: this.max })
        } else if (this.min !== null) {
          return validMin ? '' : this.$t('compartilhados.validations.Validate.maiorQue', { min: this.min })
        }
      }
      return ''
    },
    minDateMaxDateError () {
      if (this.componentType !== 'PDatePicker' || (!this.minDate && !this.maxDate)) return

      const date = this.state ? obterDataComHoraInicial(this.state) : null
      const min = this.minDate ? obterDataComHoraInicial(this.minDate) : null
      const max = this.maxDate ? obterDataComHoraInicial(this.maxDate) : null

      if (min && date && moment(date).isBefore(min)) {
        return this.$t('compartilhados.validations.Validate.posteriorData', { data: moment(min).format('DD/MM/YYYY') })
      } else if (max && date && moment(date).isAfter(max)) {
        return this.$t('compartilhados.validations.Validate.anteriorData', { data: moment(max).format('DD/MM/YYYY') })
      }
      return ''
    },
    lengthError () {
      if (!COMPONENTES_DE_TEXTO.includes(this.componentType) || (!this.required && !this.state)) return ''

      const text = this.state

      if (this.maxLength !== null && text && text.length > this.maxLength) {
        return this.$t('compartilhados.validations.Validate.maximoCaracteres', { max: this.maxLength })
      } else if (this.minLength !== null && (!text || text.length < this.minLength)) {
        return this.$t('compartilhados.validations.Validate.minimoCaracteres', { min: this.minLength })
      }
      return ''
    },
    onlyNumbersError () {
      if (!COMPONENTES_DE_TEXTO.includes(this.componentType) || !this.onlyNumbers) return ''

      const inputText = this.state
      const regex = /\D/g
      if (regex.test(inputText)) {
        return this.$t('compartilhados.validations.Validate.apenasNumeros')
      }
      return ''
    },
    erroValidacaoRegex () {
      if (!COMPONENTES_DE_TEXTO.includes(this.componentType) || !this.validacaoRegex) return ''

      const texto = this.state
      if (!texto) return ''
      const regex = this.validacaoRegex.regex
      if (regex.test(texto)) {
        return this.validacaoRegex.mensagemDeErro || this.$t('compartilhados.validations.Validate.formatoInvalido')
      }
      return ''
    }
  },
  methods: {
    registerOnValidationScope () {
      this.validationScope?.registerValidator?.(this)
    },
    unregisterFromValidationScope () {
      this.validationScope?.unregisterValidator?.(this)
    },
    setTouch (value) {
      this.touched = value
    },
    flattenSlotNodes (nodes) {
      return (nodes || []).flatMap((node) => {
        if (!node) return []
        if (typeof node.type === 'symbol') {
          const children = Array.isArray(node.children) ? node.children : []
          return this.flattenSlotNodes(children)
        }
        return [node]
      })
    },
    isWrappedComponentNode (node) {
      if (!node || !node.type) return false

      return typeof node.type === 'object' || typeof node.type === 'function'
    },
    getSlotDefault () {
      const slot = typeof this.$slots.default === 'function'
        ? this.$slots.default()
        : (this.$slots.default || [])

      return this.flattenSlotNodes(slot).filter((node) => {
        return this.isWrappedComponentNode(node)
      })
    },
    getWrappedComponentName (node) {
      return (
        node?.component?.type?.name ||
        node?.component?.type?.__name ||
        node?.component?.type?.__vccOpts?.name ||
        node?.type?.__vccOpts?.name ||
        node?.type?.extends?.name ||
        node?.type?.name ||
        node?.type?.__name ||
        null
      )
    },
    getWrappedPropsData (node) {
      return node?.props || {}
    },
    ensureWrappedComponentType (wrappedComponent) {
      const componentType = this.componentName || this.getWrappedComponentName(wrappedComponent)

      if (!componentType) {
        throw new Error('Validate component could not detect wrapped component')
      }

      if (!SUPORTED_COMPONENTS.includes(componentType)) {
        throw new Error(`Validate component dont suport ${componentType} components`)
      }

      this.componentType = componentType
      return componentType
    }
  },
  mounted () {
    this.registerOnValidationScope()
  },
  beforeUnmount () {
    this.unregisterFromValidationScope()
  },
  render () {
    const slotDefault = this.getSlotDefault()

    if (slotDefault.length !== 1) {
      throw new Error('Invalid slot. This component only support one slot with only one element')
    }

    const wrappedComponent = slotDefault[0]
    const wrappedProps = this.getWrappedPropsData(wrappedComponent)
    const componentType = this.ensureWrappedComponentType(wrappedComponent)

    if (
      wrappedProps?.useState ||
      wrappedProps?.value ||
      wrappedProps?.customVmodel ||
      ((componentType === 'PontotelInput' || ehComponenteSeletorImaginii(componentType)) && (
        wrappedProps?.modelValue !== undefined ||
        wrappedProps?.['onUpdate:modelValue']
      ))
    ) {
      throw new Error('The Validation component is the one that you must pass the useState, vmodel or customvmodel')
    }

    const erroVisivel = this.touched ? this.error : ''
    let wrappedComponentWithValidation

    if (componentType === 'PontotelInput' || ehComponenteSeletorImaginii(componentType)) {
      const propriedadesDeErro = COMPONENTES_CATALOGO.includes(componentType)
        ? { mensagemErro: erroVisivel }
        : { erro: erroVisivel }
      wrappedComponentWithValidation = cloneVNode(wrappedComponent, {
        modelValue: this.state,
        'onUpdate:modelValue': (value) => {
          this.setTouch(true)
          this.state = value
        },
        ...propriedadesDeErro
      })
    } else {
      wrappedComponentWithValidation = cloneVNode(wrappedComponent, {
        customVmodel: {
          set: (value) => {
            this.setTouch(COMPONENTES_QUE_MARCAM_TOQUE_AO_ATUALIZAR.includes(componentType))
            this.state = value
          },
          get: () => {
            return this.state
          }
        },
        ...(COMPONENTES_COM_ERRO_INTERNO.includes(componentType) ? { error: erroVisivel } : {})
      })
    }

    const children = [wrappedComponentWithValidation]

    if (!COMPONENTES_COM_ERRO_INTERNO.includes(componentType)) {
      children.push(h(ErrorMsg, {
        error: erroVisivel,
        keep: true
      }))
    }

    return h('span', {
      class: {
        'validate': true,
        'error': Boolean(this.touched && this.error)
      }
    }, children)
  },
  mixins: [
    handlerUseStateVmodel(false)
  ],
  components: { ErrorMsg }
}
</script>

<style lang="scss">
.validate {

  // hide all styles
  >.error-msg {
    display: block !important;
  }

  // Essa classe está sendo usada pelo PTextField que faz o uso desse componente
  .campoBloqueadoSankhya~.error-msg {
    display: none !important;
  }

  .error-msg {
    display: none;
  }

  &.error {

    input,
    .multiselect__tags {
      border-color: var(--color-danger) !important;
    }

    .multiselect__placeholder {
      color: var(--color-danger) !important;
    }
  }
}
</style>
